import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { IafNotification, IafScripts, IafFetch, IafUserGroup } from '../../services/platformApi';
import {
  getEmptyGroupForm,
  getEmptyDeviceForm,
  getEmptyTemplateForm,
  getEmptyTriggerForm,
  getEmptySenderForm,
  DEFAULT_SUBSCRIBE_VERIFY_TEMPLATE,
  DEFAULT_ANOMALY_TEMPLATE,
  EVENT_TYPES,
  RESOURCE_TYPES,
} from './constants';
import type {
  NotificationGroup,
  NotificationDevice,
  NotificationSubscription,
  NotificationTemplate,
  NotificationTrigger,
  NotificationSender,
  GroupFormData,
  DeviceFormData,
  TemplateFormData,
  TriggerFormData,
  SenderFormData,
} from './types';
import './NotificationManagementPage.css';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

// User type for the user group members dropdown
interface UserGroupMember {
  _id: string;
  _email?: string;
  _firstName?: string;
  _lastName?: string;
  _displayName?: string;
}

export function NotificationManagementPage() {
  const { accessToken, user } = useAuth();
  const { selectedProject, selectedUserGroup } = useApp();

  // Active tab - start with templates (step 1)
  const [activeTab, setActiveTab] = useState<'groups' | 'devices' | 'subscriptions' | 'templates' | 'triggers' | 'senders'>('templates');
  
  // User group members for device creation
  const [userGroupMembers, setUserGroupMembers] = useState<UserGroupMember[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  // Data state
  const [groups, setGroups] = useState<NotificationGroup[]>([]);
  const [devices, setDevices] = useState<NotificationDevice[]>([]);
  const [subscriptions, setSubscriptions] = useState<NotificationSubscription[]>([]);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [triggers, setTriggers] = useState<NotificationTrigger[]>([]);
  const [senders, setSenders] = useState<NotificationSender[]>([]);
  const [scripts, setScripts] = useState<Array<{ _id: string; _userType?: string; _name?: string; _description?: string; _tipId?: string; _versions?: Array<{ _id: string }> }>>([]);

  // Loading and error state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editingSenderId, setEditingSenderId] = useState<string | null>(null);
  const [editingTriggerId, setEditingTriggerId] = useState<string | null>(null);
  const [groupForm, setGroupForm] = useState<GroupFormData>(getEmptyGroupForm());
  const [deviceForm, setDeviceForm] = useState<DeviceFormData>(getEmptyDeviceForm());
  const [templateForm, setTemplateForm] = useState<TemplateFormData>(getEmptyTemplateForm());
  const [triggerForm, setTriggerForm] = useState<TriggerFormData>(getEmptyTriggerForm());
  const [senderForm, setSenderForm] = useState<SenderFormData>(getEmptySenderForm());
  const [subscriptionGroupId, setSubscriptionGroupId] = useState('');
  const [subscriptionDeviceId, setSubscriptionDeviceId] = useState('');

  // Namespace creation state (namespaces are immutable after creation)
  const [creationNamespaces, setCreationNamespaces] = useState<string[]>([]);
  const [newNamespaceInput, setNewNamespaceInput] = useState('');

  // Get single project namespace (notification service requires single namespace)
  const getProjectNamespace = useCallback((): string[] => {
    const namespaces = selectedProject?._namespaces || [];
    // Return only the first namespace (project namespace)
    return namespaces.length > 0 ? [namespaces[0]] : [];
  }, [selectedProject]);

  // Create context for API calls - use ONLY project namespace (no baseNamespaces)
  const getCtx = useCallback(() => {
    const namespaces = getProjectNamespace();
    return {
      authToken: accessToken || '',
      _namespaces: namespaces,
    };
  }, [accessToken, getProjectNamespace]);

  // ============================================================================
  // API CALLS - Using IafNotification
  // ============================================================================

  const fetchGroups = useCallback(async () => {
    if (!accessToken || !selectedProject) return;

    try {
      const ctx = getCtx();
      const namespaces = getProjectNamespace();
      // Use 'namespaces' query param for notification service
      const result = await IafNotification.listGroups({ namespaces: namespaces[0] }, ctx, { _pageSize: 100 });
      setGroups(result._list || []);
    } catch (err: unknown) {
      console.error('Error fetching groups:', err);
      // If 403, show message but don't crash
      const errorObj = err as { status?: number };
      if (errorObj?.status === 403) {
        console.warn('No permission to list groups. You may still be able to create new ones.');
      }
      setGroups([]);
    }
  }, [accessToken, selectedProject, getCtx, getProjectNamespace]);

  const fetchDevices = useCallback(async (members?: UserGroupMember[]) => {
    if (!accessToken || !selectedProject) return;

    try {
      const ctx = getCtx();
      const allDevices: NotificationDevice[] = [];
      const seenDeviceIds = new Set<string>();
      
      // Use passed members or fall back to state
      const membersToUse = members || userGroupMembers;
      
      // First, always fetch current user's devices (minimum working code)
      if (user) {
        const ownerIrn = `passportsvc:user:${user._id}`;
        console.log('Fetching devices for current user:', ownerIrn);
        try {
          const result = await IafNotification.listDevices({ owner: { irn: ownerIrn } }, ctx, { _pageSize: 100 });
          (result._list || []).forEach(device => {
            if (!seenDeviceIds.has(device._id)) {
              seenDeviceIds.add(device._id);
              allDevices.push(device);
            }
          });
        } catch (err) {
          console.warn('Could not fetch devices for current user:', err);
        }
      }
      
      // Then, loop through each user in the user group and fetch their devices
      for (const member of membersToUse) {
        // Skip if this is the current user (already fetched above)
        if (user && member._id === user._id) continue;
        
        const memberIrn = `passportsvc:user:${member._id}`;
        console.log('Fetching devices for user group member:', memberIrn);
        try {
          const result = await IafNotification.listDevices({ owner: { irn: memberIrn } }, ctx, { _pageSize: 100 });
          (result._list || []).forEach(device => {
            if (!seenDeviceIds.has(device._id)) {
              seenDeviceIds.add(device._id);
              allDevices.push(device);
            }
          });
        } catch (err) {
          console.warn(`Could not fetch devices for member ${member._id}:`, err);
        }
      }
      
      console.log('Total devices fetched:', allDevices.length);
      setDevices(allDevices);
    } catch (err: unknown) {
      console.error('Error fetching devices:', err);
      const errorObj = err as { status?: number };
      if (errorObj?.status === 403) {
        console.warn('No permission to list devices.');
      }
      setDevices([]);
    }
  }, [accessToken, selectedProject, user, userGroupMembers, getCtx]);

  const fetchSubscriptions = useCallback(async () => {
    if (!accessToken || !selectedProject) return;

    try {
      const ctx = getCtx();
      const namespaces = getProjectNamespace();
      // Use 'namespaces' query param (not '_namespaces') for notification service
      const result = await IafNotification.listSubscriptions({ namespaces: namespaces[0] }, ctx, { _pageSize: 100 });
      setSubscriptions(result._list || []);
    } catch (err: unknown) {
      console.error('Error fetching subscriptions:', err);
      const errorObj = err as { status?: number };
      if (errorObj?.status === 403) {
        console.warn('No permission to list subscriptions.');
      }
      setSubscriptions([]);
    }
  }, [accessToken, selectedProject, getCtx, getProjectNamespace]);

  const fetchTemplates = useCallback(async () => {
    if (!accessToken || !selectedProject) return;

    try {
      const ctx = getCtx();
      const namespaces = getProjectNamespace();
      // Use 'namespaces' query param (not '_namespaces') for notification service
      const result = await IafNotification.listTemplates({ namespaces: namespaces[0] }, ctx, { _pageSize: 100 });
      setTemplates(result._list || []);
    } catch (err: unknown) {
      console.error('Error fetching templates:', err);
      const errorObj = err as { status?: number };
      if (errorObj?.status === 403) {
        console.warn('No permission to list templates.');
      }
      setTemplates([]);
    }
  }, [accessToken, selectedProject, getCtx, getProjectNamespace]);

  const fetchTriggers = useCallback(async () => {
    if (!accessToken || !selectedProject) return;

    try {
      const ctx = getCtx();
      const namespaces = getProjectNamespace();
      const result = await IafNotification.listTriggers({ namespaces: namespaces[0] }, ctx, { _pageSize: 100 });
      setTriggers(result._list || []);
    } catch (err: unknown) {
      console.error('Error fetching triggers:', err);
      const errorObj = err as { status?: number };
      if (errorObj?.status === 403) {
        console.warn('No permission to list triggers.');
      }
      setTriggers([]);
    }
  }, [accessToken, selectedProject, getCtx, getProjectNamespace]);

  const fetchSenders = useCallback(async () => {
    if (!accessToken || !selectedProject) return;

    try {
      const ctx = getCtx();
      const namespaces = getProjectNamespace();
      const result = await IafNotification.listSenders({ namespaces: namespaces[0] }, ctx, { _pageSize: 100 });
      setSenders(result._list || []);
    } catch (err: unknown) {
      console.error('Error fetching senders:', err);
      const errorObj = err as { status?: number };
      if (errorObj?.status === 403) {
        console.warn('No permission to list senders.');
      }
      setSenders([]);
    }
  }, [accessToken, selectedProject, getCtx, getProjectNamespace]);

  // Fetch scripts for trigger script selection
  const fetchScripts = useCallback(async () => {
    if (!accessToken || !selectedProject) return;

    try {
      const ctx = getCtx();
      // Get scripts from the project namespace
      const result = await IafScripts.getScripts({}, ctx, { _pageSize: 100 });
      // Filter to only show scripts that have a _userType (notification scripts)
      const scriptsList = Array.isArray(result) ? result : (result as { _list?: unknown[] })?._list || [];
      console.log('Fetched scripts:', scriptsList); // Debug: see what properties are available
      setScripts(scriptsList as Array<{ _id: string; _userType?: string; _name?: string; _description?: string; _tipId?: string; _versions?: Array<{ _id: string }> }>);
    } catch (err: unknown) {
      console.error('Error fetching scripts:', err);
      setScripts([]);
    }
  }, [accessToken, selectedProject, getCtx]);

  // Fetch users from the selected user group for device creation
  // Returns the members list so it can be used immediately
  const fetchUserGroupMembers = useCallback(async (): Promise<UserGroupMember[]> => {
    if (!accessToken || !selectedUserGroup) {
      setUserGroupMembers([]);
      return [];
    }

    try {
      const ctx = getCtx();
      // IafUserGroup.getUsers expects the group object
      const users = await IafUserGroup.getUsers(selectedUserGroup, ctx);
      console.log('Fetched user group members:', users);
      // Users can be an array or have a _list property
      const usersList = Array.isArray(users) ? users : (users as { _list?: UserGroupMember[] })?._list || [];
      setUserGroupMembers(usersList);
      return usersList;
    } catch (err: unknown) {
      console.error('Error fetching user group members:', err);
      setUserGroupMembers([]);
      return [];
    }
  }, [accessToken, selectedUserGroup, getCtx]);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // First, fetch user group members (needed for device queries)
      const members = await fetchUserGroupMembers();
      
      // Then fetch everything else in parallel, passing members to fetchDevices
      await Promise.all([
        fetchGroups(),
        fetchDevices(members),
        fetchSubscriptions(),
        fetchTemplates(),
        fetchTriggers(),
        fetchSenders(),
        fetchScripts(),
      ]);
    } catch {
      setError('Failed to fetch notification data');
    } finally {
      setIsLoading(false);
    }
  }, [fetchGroups, fetchDevices, fetchSubscriptions, fetchTemplates, fetchTriggers, fetchSenders, fetchScripts, fetchUserGroupMembers]);

  // Create operations
  const createGroup = async () => {
    if (!groupForm.name) {
      setError('Group name is required');
      return;
    }

    if (creationNamespaces.length === 0) {
      setError('At least one namespace is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();

      await IafNotification.createGroup({
        _name: groupForm.name,
        _description: groupForm.description,
        _namespaces: creationNamespaces,
      }, ctx);

      setSuccessMessage('Group created successfully!');
      setIsCreating(false);
      setGroupForm(getEmptyGroupForm());
      await fetchGroups();
    } catch (err) {
      setError(`Failed to create group: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const createDevice = async () => {
    // For EMAIL transport, we need a selected user from the user group
    if (deviceForm.transport === 'EMAIL') {
      if (!selectedUserId) {
        setError('Please select a user from the user group');
        return;
      }
      
      const selectedMember = userGroupMembers.find(u => u._id === selectedUserId);
      if (!selectedMember || !selectedMember._email) {
        setError('Selected user does not have an email address');
        return;
      }
    } else {
      // For non-EMAIL transports, still require address
      if (!deviceForm.address) {
        setError('Address is required');
        return;
      }
    }

    if (!user) {
      setError('User not authenticated');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();

      // Build device data based on transport type
      const deviceData: Record<string, unknown> = {
        _transport: deviceForm.transport,
      };
      
      // For EMAIL, use the selected user's data
      if (deviceForm.transport === 'EMAIL') {
        const selectedMember = userGroupMembers.find(u => u._id === selectedUserId);
        const ownerIrn = `passportsvc:user:${selectedMember!._id}`;
        deviceData._owner = { _irn: ownerIrn };
        deviceData._email = { _address: selectedMember!._email };
      } else {
        // For non-EMAIL transports, use current user as owner
        const ownerIrn = deviceForm.ownerIrn || `passportsvc:user:${user._id}`;
        deviceData._owner = { _irn: ownerIrn };
        
        if (deviceForm.transport === 'WEBHOOK') {
          deviceData._webhook = { _url: deviceForm.address };
        } else if (deviceForm.transport === 'WEBSOCKET') {
          deviceData._websocket = { _url: deviceForm.address };
        } else if (deviceForm.transport === 'PUSH') {
          deviceData._push = { _token: deviceForm.address };
        }
      }

      await IafNotification.createDevice(deviceData, ctx);

      setSuccessMessage('Device created successfully!');
      setIsCreating(false);
      setDeviceForm(getEmptyDeviceForm());
      setSelectedUserId(''); // Reset selected user
      await fetchDevices();
    } catch (err) {
      setError(`Failed to create device: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const createSubscription = async () => {
    if (!subscriptionGroupId || !subscriptionDeviceId) {
      setError('Group and device are required');
      return;
    }

    if (creationNamespaces.length === 0) {
      setError('At least one namespace is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();

      await IafNotification.createSubscription({
        _namespaces: creationNamespaces,
        _group: { _id: subscriptionGroupId },
        _device: { _id: subscriptionDeviceId },
      }, ctx);

      setSuccessMessage('Subscription created successfully!');
      setIsCreating(false);
      setSubscriptionGroupId('');
      setSubscriptionDeviceId('');
      await fetchSubscriptions();
    } catch (err) {
      setError(`Failed to create subscription: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const createTemplate = async () => {
    if (!templateForm.name || !templateForm.subject) {
      setError('Name and subject are required');
      return;
    }

    if (creationNamespaces.length === 0) {
      setError('At least one namespace is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();

      // Build template data based on transport type
      const templateData: Record<string, unknown> = {
        _name: templateForm.name,
        _description: templateForm.description,
        _transport: templateForm.transport,
        _format: templateForm.format,
        _locale: 'en',  // Required field
        _namespaces: creationNamespaces,
      };

      // Transport-specific fields - subject/body go inside transport object
      if (templateForm.transport === 'EMAIL') {
        templateData._email = {
          _subject: templateForm.subject,
          _body: templateForm.body,
        };
      } else if (templateForm.transport === 'WEBHOOK') {
        templateData._webhook = {
          _body: templateForm.body,
        };
      } else if (templateForm.transport === 'WEBSOCKET') {
        templateData._websocket = {
          _body: templateForm.body,
        };
      } else if (templateForm.transport === 'PUSH') {
        templateData._push = {
          _title: templateForm.subject,
          _body: templateForm.body,
        };
      }

      await IafNotification.createTemplate(templateData, ctx);

      setSuccessMessage('Template created successfully!');
      setIsCreating(false);
      setTemplateForm(getEmptyTemplateForm());
      await fetchTemplates();
    } catch (err) {
      setError(`Failed to create template: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const createDefaultSubscribeVerifyTemplate = () => {
    setTemplateForm(DEFAULT_SUBSCRIBE_VERIFY_TEMPLATE);
    initCreationNamespaces();
    setActiveTab('templates');
    setIsCreating(true);
  };

  const createDefaultAnomalyTemplate = () => {
    setTemplateForm(DEFAULT_ANOMALY_TEMPLATE);
    initCreationNamespaces();
    setActiveTab('templates');
    setIsCreating(true);
  };

  // Delete operations - pass namespaces in options for notification service
  const deleteGroup = async (group: NotificationGroup) => {
    if (!confirm(`Delete group "${group._name}"?`)) return;

    try {
      const ctx = getCtx();
      const namespaces = getProjectNamespace();
      await IafNotification.deleteGroup(group._id, ctx, { namespaces: namespaces[0] });
      setSuccessMessage('Group deleted');
      await fetchGroups();
    } catch (err) {
      console.error('Error deleting group:', err);
      setError('Failed to delete group');
    }
  };

  // Helper to get device display address based on transport type
  const getDeviceAddress = (device: NotificationDevice): string => {
    if (device._email?._address) return device._email._address;
    if (device._webhook?._url) return device._webhook._url;
    if (device._websocket?._url) return device._websocket._url;
    if (device._push?._token) return device._push._token;
    return device._id;
  };

  const deleteDevice = async (device: NotificationDevice) => {
    if (!confirm(`Delete device "${getDeviceAddress(device)}"?`)) return;

    try {
      const ctx = getCtx();
      // Devices are filtered by owner, not namespace
      await IafNotification.deleteDevice(device._id, ctx);
      setSuccessMessage('Device deleted');
      await fetchDevices();
    } catch (err) {
      console.error('Error deleting device:', err);
      setError('Failed to delete device');
    }
  };

  const deleteSubscription = async (sub: NotificationSubscription) => {
    if (!confirm('Delete this subscription?')) return;

    try {
      const ctx = getCtx();
      const namespaces = getProjectNamespace();
      await IafNotification.deleteSubscription(sub._id, ctx, { namespaces: namespaces[0] });
      setSuccessMessage('Subscription deleted');
      await fetchSubscriptions();
    } catch (err) {
      console.error('Error deleting subscription:', err);
      setError('Failed to delete subscription');
    }
  };

  const deleteTemplate = async (template: NotificationTemplate) => {
    if (!confirm(`Delete template "${template._name}"?`)) return;

    try {
      const ctx = getCtx();
      const namespaces = getProjectNamespace();
      // Pass namespaces in options for notification service
      await IafNotification.deleteTemplate(template._id, ctx, { namespaces: namespaces[0] });
      setSuccessMessage('Template deleted');
      await fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Failed to delete template');
    }
  };

  const editTemplate = (template: NotificationTemplate) => {
    // Populate form with existing template data
    setTemplateForm({
      name: template._name || '',
      description: template._description || '',
      transport: (template._transport as 'EMAIL' | 'PUSH' | 'WEBSOCKET' | 'WEBHOOK') || 'EMAIL',
      format: (template._format as 'VELOCITY' | 'HANDLEBARS') || 'VELOCITY',
      subject: template._email?._subject || '',
      body: template._email?._body || '',
    });
    setEditingTemplateId(template._id);
    setIsEditing(true);
    setActiveTab('templates');
  };

  const updateTemplate = async () => {
    if (!editingTemplateId) return;
    if (!templateForm.name || !templateForm.subject) {
      setError('Name and subject are required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      const namespaces = getProjectNamespace();

      // Build template data based on transport type
      const templateData: Record<string, unknown> = {
        _name: templateForm.name,
        _description: templateForm.description,
        _transport: templateForm.transport,
        _format: templateForm.format,
        _locale: 'en',
        _namespaces: namespaces,
      };

      // Transport-specific fields
      if (templateForm.transport === 'EMAIL') {
        templateData._email = {
          _subject: templateForm.subject,
          _body: templateForm.body,
        };
      } else if (templateForm.transport === 'WEBHOOK') {
        templateData._webhook = { _body: templateForm.body };
      } else if (templateForm.transport === 'WEBSOCKET') {
        templateData._websocket = { _body: templateForm.body };
      } else if (templateForm.transport === 'PUSH') {
        templateData._push = { _title: templateForm.subject, _body: templateForm.body };
      }

      await IafNotification.updateTemplate(editingTemplateId, templateData, ctx, { namespaces: namespaces[0] });

      setSuccessMessage('Template updated successfully!');
      setIsEditing(false);
      setEditingTemplateId(null);
      setTemplateForm(getEmptyTemplateForm());
      await fetchTemplates();
    } catch (err) {
      console.error('Error updating template:', err);
      setError(`Failed to update template: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const createTrigger = async () => {
    if (!triggerForm.name) {
      setError('Trigger name is required');
      return;
    }

    if (triggerForm.groupIds.length === 0) {
      setError('At least one group is required');
      return;
    }

    if (triggerForm.templateIds.length === 0) {
      setError('At least one template is required');
      return;
    }

    if (creationNamespaces.length === 0) {
      setError('At least one namespace is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();

      // Build filters - using correct property names from API docs
      const filters: Record<string, string> = {
        event: triggerForm.eventType,  // "event" not "type"
        'resource.type': triggerForm.resourceType,
      };
      
      // Add namespace filter if specified
      if (triggerForm.resourceNamespace) {
        filters['resource.namespaces'] = triggerForm.resourceNamespace;
      }

      const triggerData: Record<string, unknown> = {
        _name: triggerForm.name,
        _type: triggerForm.type,
        _namespaces: creationNamespaces,
        // Event filters nested in _event object per API docs
        _event: {
          _filters: filters,
        },
        _groups: triggerForm.groupIds.map(id => ({ _id: id })),
        _templates: triggerForm.templateIds.map(id => ({ _id: id })),
      };

      // Add senders if any selected
      if (triggerForm.senderIds.length > 0) {
        triggerData._senders = triggerForm.senderIds.map(id => ({ _id: id }));
      }

      // Add script if selected (using _userType to find the script)
      // API expects: id, userType, name, versionId (without underscores inside _script)
      if (triggerForm.scriptUserType) {
        const selectedScript = scripts.find(s => s._userType === triggerForm.scriptUserType);
        if (selectedScript) {
          if (!triggerForm.scriptFunctionName) {
            setError('Script function name is required when a script is selected');
            setIsLoading(false);
            return;
          }
          if (!triggerForm.scriptVersionId) {
            setError('Script version ID is required when a script is selected');
            setIsLoading(false);
            return;
          }
          triggerData._script = { 
            id: selectedScript._id,
            userType: selectedScript._userType,
            name: triggerForm.scriptFunctionName,
            versionId: triggerForm.scriptVersionId,
          };
          console.log('Adding script to trigger:', triggerData._script);
        } else {
          console.warn('Script with userType not found:', triggerForm.scriptUserType);
        }
      } else {
        console.log('No script selected for trigger');
      }

      console.log('Creating trigger with data:', JSON.stringify(triggerData, null, 2));
      await IafNotification.createTrigger(triggerData, ctx);

      setSuccessMessage('Trigger created successfully!');
      setIsCreating(false);
      setTriggerForm(getEmptyTriggerForm());
      await fetchTriggers();
    } catch (err) {
      console.error('Error creating trigger:', err);
      setError(`Failed to create trigger: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTrigger = async (trigger: NotificationTrigger) => {
    if (!confirm(`Delete trigger "${trigger._name}"?`)) return;

    try {
      const ctx = getCtx();
      const namespaces = getProjectNamespace();
      await IafNotification.deleteTrigger(trigger._id, ctx, { namespaces: namespaces[0] });
      setSuccessMessage('Trigger deleted');
      await fetchTriggers();
    } catch (err) {
      console.error('Error deleting trigger:', err);
      setError('Failed to delete trigger');
    }
  };

  const createSender = async () => {
    if (!senderForm.emailAddress) {
      setError('Email address is required');
      return;
    }

    if (creationNamespaces.length === 0) {
      setError('At least one namespace is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();

      const senderData: Record<string, unknown> = {
        _namespaces: creationNamespaces,
        _transport: senderForm.transport,
        _name: senderForm.displayName || senderForm.emailAddress, // Name is required
      };

      if (senderForm.transport === 'EMAIL') {
        senderData._email = {
          _address: senderForm.emailAddress,
          _displayName: senderForm.displayName || undefined,
        };
      }

      // Add SUBSCRIBE_VERIFY template if selected
      if (senderForm.subscribeVerifyTemplateId) {
        senderData._templates = {
          SUBSCRIBE_VERIFY: { _id: senderForm.subscribeVerifyTemplateId },
        };
      }

      // Add redirect URIs
      senderData._redirectUris = {
        SUBSCRIBE_SUCCESS: senderForm.redirectUris.subscribeSuccess,
        SUBSCRIBE_FAILURE: senderForm.redirectUris.subscribeFailure,
        UNSUBSCRIBE_SUCCESS: senderForm.redirectUris.unsubscribeSuccess,
        UNSUBSCRIBE_FAILURE: senderForm.redirectUris.unsubscribeFailure,
        UNSUBSCRIBE_ALL_SUCCESS: senderForm.redirectUris.unsubscribeAllSuccess,
        UNSUBSCRIBE_ALL_FAILURE: senderForm.redirectUris.unsubscribeAllFailure,
      };

      console.log('Creating sender with data:', JSON.stringify(senderData, null, 2));
      await IafNotification.createSender(senderData, ctx);

      setSuccessMessage('Sender created successfully!');
      setIsCreating(false);
      setSenderForm(getEmptySenderForm());
      await fetchSenders();
    } catch (err) {
      console.error('Error creating sender:', err);
      setError(`Failed to create sender: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSender = async (sender: NotificationSender) => {
    const senderName = sender._email?._displayName || sender._email?._address || sender._id;
    if (!confirm(`Delete sender "${senderName}"?`)) return;

    try {
      const ctx = getCtx();
      const namespaces = getProjectNamespace();
      await IafNotification.deleteSender(sender._id, ctx, { namespaces: namespaces[0] });
      setSuccessMessage('Sender deleted');
      await fetchSenders();
    } catch (err) {
      console.error('Error deleting sender:', err);
      setError('Failed to delete sender');
    }
  };

  const editSender = (sender: NotificationSender) => {
    // Populate form with existing sender data
    setSenderForm({
      transport: (sender._transport as 'EMAIL' | 'PUSH' | 'WEBSOCKET' | 'WEBHOOK') || 'EMAIL',
      emailAddress: sender._email?._address || '',
      displayName: sender._email?._displayName || sender._name || '',
      subscribeVerifyTemplateId: sender._templates?.SUBSCRIBE_VERIFY?._id || '',
      redirectUris: {
        subscribeSuccess: sender._redirectUris?.SUBSCRIBE_SUCCESS || window.location.origin + '/subscribed',
        subscribeFailure: sender._redirectUris?.SUBSCRIBE_FAILURE || window.location.origin + '/subscribe-error',
        unsubscribeSuccess: sender._redirectUris?.UNSUBSCRIBE_SUCCESS || window.location.origin + '/unsubscribed',
        unsubscribeFailure: sender._redirectUris?.UNSUBSCRIBE_FAILURE || window.location.origin + '/unsubscribe-error',
        unsubscribeAllSuccess: sender._redirectUris?.UNSUBSCRIBE_ALL_SUCCESS || window.location.origin + '/unsubscribed-all',
        unsubscribeAllFailure: sender._redirectUris?.UNSUBSCRIBE_ALL_FAILURE || window.location.origin + '/unsubscribe-all-error',
      },
    });
    setEditingSenderId(sender._id);
    setIsEditing(true);
    setActiveTab('senders');
  };

  const updateSender = async () => {
    if (!editingSenderId) return;
    if (!senderForm.emailAddress) {
      setError('Email address is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      const namespaces = getProjectNamespace();

      const senderData: Record<string, unknown> = {
        _name: senderForm.displayName || senderForm.emailAddress,
      };

      if (senderForm.transport === 'EMAIL') {
        senderData._email = {
          _address: senderForm.emailAddress,
          _displayName: senderForm.displayName || undefined,
        };
      }

      // Update SUBSCRIBE_VERIFY template reference
      if (senderForm.subscribeVerifyTemplateId) {
        senderData._templates = {
          SUBSCRIBE_VERIFY: { _id: senderForm.subscribeVerifyTemplateId },
        };
      } else {
        // Clear the template if none selected
        senderData._templates = {};
      }

      // Update redirect URIs
      senderData._redirectUris = {
        SUBSCRIBE_SUCCESS: senderForm.redirectUris.subscribeSuccess,
        SUBSCRIBE_FAILURE: senderForm.redirectUris.subscribeFailure,
        UNSUBSCRIBE_SUCCESS: senderForm.redirectUris.unsubscribeSuccess,
        UNSUBSCRIBE_FAILURE: senderForm.redirectUris.unsubscribeFailure,
        UNSUBSCRIBE_ALL_SUCCESS: senderForm.redirectUris.unsubscribeAllSuccess,
        UNSUBSCRIBE_ALL_FAILURE: senderForm.redirectUris.unsubscribeAllFailure,
      };

      console.log('Updating sender with data:', JSON.stringify(senderData, null, 2));
      await IafNotification.updateSender(editingSenderId, senderData, ctx, { namespaces: namespaces[0] });

      setSuccessMessage('Sender updated successfully!');
      setIsEditing(false);
      setEditingSenderId(null);
      setSenderForm(getEmptySenderForm());
      await fetchSenders();
    } catch (err) {
      console.error('Error updating sender:', err);
      setError(`Failed to update sender: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Edit trigger
  const editTrigger = (trigger: NotificationTrigger) => {
    // Populate form with existing trigger data
    // Note: API returns script properties without underscores (id, name, userType, versionId)
    const scriptData = trigger._script as { id?: string; name?: string; userType?: string; versionId?: string } | undefined;
    setTriggerForm({
      name: trigger._name || '',
      type: (trigger._type as 'EVENT' | 'SCHEDULE') || 'EVENT',
      scriptUserType: scriptData?.userType || '', // Load from trigger response
      scriptFunctionName: scriptData?.name || 'processNotifications', // Load existing function name or default
      scriptVersionId: scriptData?.versionId || '', // Load existing version ID
      eventType: trigger._event?._filters?.event || '',
      resourceType: trigger._event?._filters?.['resource.type'] || '',
      resourceNamespace: trigger._event?._filters?.['resource.namespaces'] || '',
      groupIds: trigger._groups?.map(g => g._id) || [],
      templateIds: trigger._templates?.map(t => t._id) || [],
      senderIds: trigger._senders?.map(s => s._id) || [],
    });
    
    // If userType wasn't in the response, try to find it from script ID
    if (!scriptData?.userType && scriptData?.id) {
      const script = scripts.find(s => s._id === scriptData.id);
      if (script?._userType) {
        setTriggerForm(prev => ({ ...prev, scriptUserType: script._userType || '' }));
      }
    }
    
    setEditingTriggerId(trigger._id);
    setIsEditing(true);
    setActiveTab('triggers');
  };

  const updateTrigger = async () => {
    if (!editingTriggerId) return;
    if (!triggerForm.name) {
      setError('Trigger name is required');
      return;
    }
    if (triggerForm.groupIds.length === 0) {
      setError('At least one group is required');
      return;
    }
    if (triggerForm.templateIds.length === 0) {
      setError('At least one template is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      const namespaces = getProjectNamespace();

      const triggerData: Record<string, unknown> = {
        _name: triggerForm.name,
        _type: triggerForm.type,
        _groups: triggerForm.groupIds.map(id => ({ _id: id })),
        _templates: triggerForm.templateIds.map(id => ({ _id: id })),
      };

      // Add senders if selected
      if (triggerForm.senderIds.length > 0) {
        triggerData._senders = triggerForm.senderIds.map(id => ({ _id: id }));
      }

      // Add event filters if event type
      if (triggerForm.type === 'EVENT') {
        triggerData._event = {
          _filters: {
            event: triggerForm.eventType || undefined,
            'resource.type': triggerForm.resourceType || undefined,
            'resource.namespaces': triggerForm.resourceNamespace || undefined,
          },
        };
      }

      // Add script reference if selected (using _userType to find the script)
      // API expects: id, userType, name, versionId (without underscores inside _script)
      if (triggerForm.scriptUserType) {
        const selectedScript = scripts.find(s => s._userType === triggerForm.scriptUserType);
        if (selectedScript) {
          if (!triggerForm.scriptFunctionName) {
            setError('Script function name is required when a script is selected');
            setIsLoading(false);
            return;
          }
          if (!triggerForm.scriptVersionId) {
            setError('Script version ID is required when a script is selected');
            setIsLoading(false);
            return;
          }
          triggerData._script = { 
            id: selectedScript._id,
            userType: selectedScript._userType,
            name: triggerForm.scriptFunctionName,
            versionId: triggerForm.scriptVersionId,
          };
          console.log('Adding script to trigger (PATCH):', triggerData._script);
        }
      }

      console.log('Updating trigger with data:', JSON.stringify(triggerData, null, 2));
      await IafNotification.updateTrigger(editingTriggerId, triggerData, ctx, { namespaces: namespaces[0] });

      setSuccessMessage('Trigger updated successfully!');
      setIsEditing(false);
      setEditingTriggerId(null);
      setTriggerForm(getEmptyTriggerForm());
      await fetchTriggers();
    } catch (err) {
      console.error('Error updating trigger:', err);
      setError(`Failed to update trigger: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Update trigger using PUT (instead of PATCH) - use if CORS issues with PATCH
  const updateTriggerWithPut = async () => {
    if (!editingTriggerId) return;
    if (!triggerForm.name) {
      setError('Trigger name is required');
      return;
    }
    if (triggerForm.groupIds.length === 0) {
      setError('At least one group is required');
      return;
    }
    if (triggerForm.templateIds.length === 0) {
      setError('At least one template is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const namespaces = getProjectNamespace();

      // For PUT, we need to send the complete object including namespaces
      const triggerData: Record<string, unknown> = {
        _name: triggerForm.name,
        _type: triggerForm.type,
        _namespaces: namespaces, // Required for PUT
        _groups: triggerForm.groupIds.map(id => ({ _id: id })),
        _templates: triggerForm.templateIds.map(id => ({ _id: id })),
      };

      // Add senders if selected
      if (triggerForm.senderIds.length > 0) {
        triggerData._senders = triggerForm.senderIds.map(id => ({ _id: id }));
      }

      // Add event filters if event type
      if (triggerForm.type === 'EVENT') {
        triggerData._event = {
          _filters: {
            event: triggerForm.eventType || undefined,
            'resource.type': triggerForm.resourceType || undefined,
            'resource.namespaces': triggerForm.resourceNamespace || undefined,
          },
        };
      }

      // Add script reference if selected
      // API expects: id, userType, name, versionId (without underscores inside _script)
      if (triggerForm.scriptUserType) {
        const selectedScript = scripts.find(s => s._userType === triggerForm.scriptUserType);
        if (selectedScript) {
          if (!triggerForm.scriptFunctionName) {
            setError('Script function name is required when a script is selected');
            setIsLoading(false);
            return;
          }
          if (!triggerForm.scriptVersionId) {
            setError('Script version ID is required when a script is selected');
            setIsLoading(false);
            return;
          }
          triggerData._script = { 
            id: selectedScript._id,
            userType: selectedScript._userType,
            name: triggerForm.scriptFunctionName,
            versionId: triggerForm.scriptVersionId,
          };
          console.log('Adding script to trigger (PUT):', triggerData._script);
        }
      }

      // Build URL for PUT request
      const baseUrl = IafFetch.CONFIG.notificationServiceOrigin;
      const url = `${baseUrl}/platform-notificationsvc/api/trigger/${editingTriggerId}?namespaces=${namespaces[0]}`;

      console.log('Updating trigger with PUT:', url, JSON.stringify(triggerData, null, 2));

      // Make PUT request directly
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(triggerData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error_description || `HTTP ${response.status}`);
      }

      setSuccessMessage('Trigger updated successfully (PUT)!');
      setIsEditing(false);
      setEditingTriggerId(null);
      setTriggerForm(getEmptyTriggerForm());
      await fetchTriggers();
    } catch (err) {
      console.error('Error updating trigger with PUT:', err);
      setError(`Failed to update trigger (PUT): ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Namespace helpers for creation forms (namespaces are immutable after creation)
  const initCreationNamespaces = useCallback(() => {
    const projectNs = getProjectNamespace();
    setCreationNamespaces(projectNs);
    setNewNamespaceInput('');
  }, [getProjectNamespace]);

  // Initialize trigger form with project namespace pre-filled
  const initTriggerForm = useCallback(() => {
    const projectNs = getProjectNamespace();
    setTriggerForm({
      ...getEmptyTriggerForm(),
      resourceNamespace: projectNs[0] || '', // Pre-fill with project namespace
    });
  }, [getProjectNamespace]);

  const addNamespaceToCreation = () => {
    if (newNamespaceInput && !creationNamespaces.includes(newNamespaceInput)) {
      setCreationNamespaces([...creationNamespaces, newNamespaceInput]);
      setNewNamespaceInput('');
    }
  };

  const removeNamespaceFromCreation = (ns: string) => {
    if (creationNamespaces.length > 1) {
      setCreationNamespaces(creationNamespaces.filter(n => n !== ns));
    } else {
      setError('At least one namespace is required');
    }
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Fetch all data when project or user group changes
  useEffect(() => {
    if (accessToken && selectedProject) {
      fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, selectedProject?._id, selectedUserGroup?._id]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!selectedProject) {
    return (
      <div className="notification-management">
        <div className="notification-empty-state">
          <i className="fas fa-exclamation-triangle"></i>
          <h2>No Project Selected</h2>
          <p>Please select a project to manage notifications.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-management">
      <div className="notification-header">
        <div className="notification-title">
          <i className="fas fa-bell"></i>
          <h1>Notification Management</h1>
        </div>
        <p className="notification-subtitle">
          Configure email alerts and notifications for <strong>{selectedProject._name}</strong>
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="notification-message error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      {successMessage && (
        <div className="notification-message success">
          <i className="fas fa-check-circle"></i>
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {/* Setup Status Dashboard */}
      <div className="setup-status-card">
        <div className="setup-status-header">
          <h2>
            <i className="fas fa-clipboard-check"></i>
            Setup Status
          </h2>
          <button className="btn-secondary btn-sm" onClick={fetchAll} disabled={isLoading}>
            <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
            Refresh
          </button>
        </div>
        
        <div className="setup-status-grid">
          {/* Step 1: Templates (SUBSCRIBE_VERIFY) */}
          <div className={`setup-status-item ${templates.length > 0 ? 'complete' : 'warning'}`}>
            <div className="status-icon">
              {templates.length > 0 ? (
                <i className="fas fa-check-circle"></i>
              ) : (
                <i className="fas fa-exclamation-triangle"></i>
              )}
            </div>
            <div className="status-content">
              <div className="status-label">1. Templates</div>
              <div className="status-value">
                {templates.length > 0 ? (
                  <span>{templates.length} template{templates.length !== 1 ? 's' : ''}</span>
                ) : (
                  <span className="warning-text">Need SUBSCRIBE_VERIFY first</span>
                )}
              </div>
            </div>
            {templates.length === 0 && (
              <button className="btn-sm btn-warning" onClick={() => { setActiveTab('templates'); setTemplateForm(getEmptyTemplateForm()); initCreationNamespaces(); setIsCreating(true); }}>
                <i className="fas fa-plus"></i> Add
              </button>
            )}
          </div>

          {/* Step 2: Senders (reference SUBSCRIBE_VERIFY) */}
          <div className={`setup-status-item ${senders.length > 0 ? 'complete' : (templates.length > 0 ? 'warning' : 'pending')}`}>
            <div className="status-icon">
              {senders.length > 0 ? (
                <i className="fas fa-check-circle"></i>
              ) : templates.length > 0 ? (
                <i className="fas fa-exclamation-triangle"></i>
              ) : (
                <i className="fas fa-circle"></i>
              )}
            </div>
            <div className="status-content">
              <div className="status-label">2. Senders</div>
              <div className="status-value">
                {senders.length > 0 ? (
                  <span>{senders.length} configured</span>
                ) : (
                  <span className={templates.length > 0 ? 'warning-text' : ''}>
                    {templates.length > 0 ? 'Not configured!' : 'Needs templates first'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Step 3: Groups */}
          <div className={`setup-status-item ${groups.length > 0 ? 'complete' : 'pending'}`}>
            <div className="status-icon">
              {groups.length > 0 ? (
                <i className="fas fa-check-circle"></i>
              ) : (
                <i className="fas fa-circle"></i>
              )}
            </div>
            <div className="status-content">
              <div className="status-label">3. Groups</div>
              <div className="status-value">{groups.length} notification group{groups.length !== 1 ? 's' : ''}</div>
            </div>
          </div>

          {/* Step 4: Devices */}
          <div className={`setup-status-item ${devices.length > 0 ? 'complete' : 'pending'}`}>
            <div className="status-icon">
              {devices.length > 0 ? (
                <i className="fas fa-check-circle"></i>
              ) : (
                <i className="fas fa-circle"></i>
              )}
            </div>
            <div className="status-content">
              <div className="status-label">4. Devices</div>
              <div className="status-value">{devices.length} recipient{devices.length !== 1 ? 's' : ''}</div>
            </div>
          </div>

          {/* Step 5: Subscriptions */}
          <div className={`setup-status-item ${subscriptions.length > 0 ? 'complete' : 'pending'}`}>
            <div className="status-icon">
              {subscriptions.length > 0 ? (
                <i className="fas fa-check-circle"></i>
              ) : (
                <i className="fas fa-circle"></i>
              )}
            </div>
            <div className="status-content">
              <div className="status-label">5. Subscriptions</div>
              <div className="status-value">{subscriptions.length} active</div>
            </div>
          </div>

          {/* Step 6: Triggers */}
          <div className={`setup-status-item ${triggers.length > 0 ? 'complete' : 'pending'}`}>
            <div className="status-icon">
              {triggers.length > 0 ? (
                <i className="fas fa-check-circle"></i>
              ) : (
                <i className="fas fa-circle"></i>
              )}
            </div>
            <div className="status-content">
              <div className="status-label">6. Triggers</div>
              <div className="status-value">{triggers.length} event watcher{triggers.length !== 1 ? 's' : ''}</div>
            </div>
          </div>
        </div>

        {/* Setup hints */}
        {templates.length === 0 && (
          <div className="setup-warning">
            <i className="fas fa-info-circle"></i>
            <span>
              <strong>Start with Templates!</strong> Create a SUBSCRIBE_VERIFY template first (with <code>$acceptLink</code>). 
              This template is sent when users subscribe to receive notifications.
              <button className="link-btn" onClick={() => { setActiveTab('templates'); setTemplateForm(getEmptyTemplateForm()); initCreationNamespaces(); setIsCreating(true); }}>
                Create template →
              </button>
            </span>
          </div>
        )}
        {templates.length > 0 && senders.length === 0 && (
          <div className="setup-warning">
            <i className="fas fa-exclamation-triangle"></i>
            <span>
              <strong>Create a Sender!</strong> The sender defines the "from" email address and references 
              your SUBSCRIBE_VERIFY template for subscription verification.
              <button className="link-btn" onClick={() => { setActiveTab('senders'); setSenderForm(getEmptySenderForm()); initCreationNamespaces(); setIsCreating(true); }}>
                Create sender →
              </button>
            </span>
          </div>
        )}
      </div>

      {/* Tabs - Organized by setup flow */}
      <div className="notification-tabs-wrapper">
        <div className="tabs-group">
          <span className="tabs-group-label">Step 1-2: Foundation</span>
          <div className="notification-tabs">
            <button
              className={`tab-btn ${activeTab === 'templates' ? 'active' : ''} ${templates.length === 0 ? 'highlight' : ''}`}
              onClick={() => setActiveTab('templates')}
            >
              <i className="fas fa-file-alt"></i>
              1. Templates ({templates.length})
              {templates.length === 0 && <span className="tab-badge warning">!</span>}
            </button>
            <button
              className={`tab-btn ${activeTab === 'senders' ? 'active' : ''} ${templates.length > 0 && senders.length === 0 ? 'highlight' : ''}`}
              onClick={() => setActiveTab('senders')}
            >
              <i className="fas fa-paper-plane"></i>
              2. Senders ({senders.length})
              {templates.length > 0 && senders.length === 0 && <span className="tab-badge warning">!</span>}
            </button>
          </div>
        </div>

        <div className="tabs-group">
          <span className="tabs-group-label">Step 3-5: Audience</span>
          <div className="notification-tabs">
            <button
              className={`tab-btn ${activeTab === 'groups' ? 'active' : ''}`}
              onClick={() => setActiveTab('groups')}
            >
              <i className="fas fa-layer-group"></i>
              3. Groups ({groups.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'devices' ? 'active' : ''}`}
              onClick={() => setActiveTab('devices')}
            >
              <i className="fas fa-envelope"></i>
              4. Devices ({devices.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'subscriptions' ? 'active' : ''}`}
              onClick={() => setActiveTab('subscriptions')}
            >
              <i className="fas fa-link"></i>
              5. Subscriptions ({subscriptions.length})
            </button>
          </div>
        </div>

        <div className="tabs-group">
          <span className="tabs-group-label">Step 6: Automation</span>
          <div className="notification-tabs">
            <button
              className={`tab-btn ${activeTab === 'triggers' ? 'active' : ''}`}
              onClick={() => setActiveTab('triggers')}
            >
              <i className="fas fa-bolt"></i>
              6. Triggers ({triggers.length})
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Groups Tab */}
        {activeTab === 'groups' && (
          <div className="section-card">
            <div className="section-header">
              <h2>
                <i className="fas fa-layer-group"></i>
                Notification Groups
              </h2>
              <button className="btn-primary" onClick={() => { setGroupForm(getEmptyGroupForm()); initCreationNamespaces(); setIsCreating(true); }}>
                <i className="fas fa-plus"></i>
                Add Group
              </button>
            </div>
            <p className="section-description">
              Groups organize notifications by topic (e.g., "Anomaly Alerts", "System Notifications")
            </p>

            {groups.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>No notification groups found</p>
              </div>
            ) : (
              <div className="items-grid">
                {groups.map((group) => (
                  <div key={group._id} className="item-card">
                    <div className="item-header">
                      <h3>{group._name}</h3>
                      <button className="btn-icon danger" onClick={() => deleteGroup(group)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                    {group._description && <p className="item-description">{group._description}</p>}
                    
                    {/* Namespaces (immutable after creation) */}
                    <div className="item-namespaces">
                      <div className="namespaces-header">
                        <span className="namespaces-label">
                          <i className="fas fa-globe"></i>
                          Namespaces
                        </span>
                      </div>
                      <div className="namespace-tags">
                        {(group._namespaces || []).map(ns => (
                          <span key={ns} className="namespace-tag">{ns}</span>
                        ))}
                        {(!group._namespaces || group._namespaces.length === 0) && (
                          <span className="namespace-tag none">No namespaces</span>
                        )}
                      </div>
                    </div>

                    <div className="item-meta">
                      <code className="item-id">{group._id}</code>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Devices Tab */}
        {activeTab === 'devices' && (
          <div className="section-card">
            <div className="section-header">
              <h2>
                <i className="fas fa-envelope"></i>
                Notification Devices
              </h2>
              <button className="btn-primary" onClick={() => { setDeviceForm(getEmptyDeviceForm()); setIsCreating(true); }}>
                <i className="fas fa-plus"></i>
                Add Device
              </button>
            </div>
            <p className="section-description">
              Devices are email addresses or endpoints that receive notifications
            </p>

            {devices.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>No notification devices found</p>
              </div>
            ) : (
              <div className="items-grid">
                {devices.map((device) => (
                  <div key={device._id} className="item-card">
                    <div className="item-header">
                      <h3>{getDeviceAddress(device)}</h3>
                      <button className="btn-icon danger" onClick={() => deleteDevice(device)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                    <div className="item-badges">
                      <span className={`badge ${device._transport.toLowerCase()}`}>
                        {device._transport}
                      </span>
                    </div>
                    <div className="item-meta">
                      <code className="item-id">{device._id}</code>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <div className="section-card">
            <div className="section-header">
              <h2>
                <i className="fas fa-link"></i>
                Subscriptions
              </h2>
              <button 
                className="btn-primary" 
                onClick={() => { initCreationNamespaces(); setIsCreating(true); }}
                disabled={groups.length === 0 || devices.length === 0}
              >
                <i className="fas fa-plus"></i>
                Add Subscription
              </button>
            </div>
            <p className="section-description">
              Subscriptions link devices to groups to determine who receives what notifications
            </p>

            {/* Sender Warning */}
            {senders.length === 0 && (
              <div className="inline-warning">
                <i className="fas fa-exclamation-triangle"></i>
                <span>
                  <strong>No Sender configured!</strong> Without a sender for this namespace, 
                  subscription verification emails won't be sent. Recipients must verify their subscriptions 
                  by email before receiving notifications.
                  <button className="link-btn" onClick={() => { setActiveTab('senders'); setSenderForm(getEmptySenderForm()); initCreationNamespaces(); setIsCreating(true); }}>
                    Create a sender →
                  </button>
                </span>
              </div>
            )}

            {groups.length === 0 || devices.length === 0 ? (
              <div className="empty-state warning">
                <i className="fas fa-exclamation-triangle"></i>
                <p>Create at least one group and one device before adding subscriptions</p>
              </div>
            ) : subscriptions.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>No subscriptions found</p>
              </div>
            ) : (
              <div className="subscriptions-list">
                {subscriptions.map((sub) => {
                  const group = groups.find(g => g._id === sub._group._id);
                  const device = devices.find(d => d._id === sub._device._id);
                  return (
                    <div key={sub._id} className="subscription-item">
                      <div className="subscription-group">
                        <i className="fas fa-layer-group"></i>
                        <span>{group?._name || sub._group._id}</span>
                      </div>
                      <i className="fas fa-arrow-right"></i>
                      <div className="subscription-device">
                        <i className="fas fa-envelope"></i>
                        <span>{device ? getDeviceAddress(device) : sub._device._id}</span>
                      </div>
                      <button className="btn-icon danger" onClick={() => deleteSubscription(sub)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="section-card">
            <div className="section-header">
              <h2>
                <i className="fas fa-file-alt"></i>
                Email Templates
              </h2>
              <button className="btn-primary" onClick={() => { setTemplateForm(getEmptyTemplateForm()); initCreationNamespaces(); setIsCreating(true); }}>
                <i className="fas fa-plus"></i>
                Add Template
              </button>
            </div>
            <p className="section-description">
              Templates define email content. You need <strong>two types</strong>:
            </p>
            <div className="template-types-info">
              <div className="template-type-box">
                <i className="fas fa-user-check"></i>
                <div>
                  <strong>SUBSCRIBE_VERIFY Template</strong>
                  <span>Used by Senders for subscription confirmation emails. Must include <code>$acceptLink</code>.</span>
                </div>
              </div>
              <div className="template-type-box">
                <i className="fas fa-bell"></i>
                <div>
                  <strong>Notification Templates</strong>
                  <span>Used by Triggers for actual event notifications. Include unsubscribe links.</span>
                </div>
              </div>
            </div>

            {templates.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>No templates found</p>
                <p className="empty-hint">Start by creating a SUBSCRIBE_VERIFY template, then create notification templates for your triggers.</p>
                <div className="empty-actions">
                  <button className="btn-primary" onClick={createDefaultSubscribeVerifyTemplate}>
                    <i className="fas fa-user-check"></i>
                    Create SUBSCRIBE_VERIFY Template
                  </button>
                  <button className="btn-secondary" onClick={createDefaultAnomalyTemplate}>
                    <i className="fas fa-bell"></i>
                    Create Anomaly Alert Template
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Quick template creation buttons */}
                <div className="template-quick-actions">
                  <span className="quick-actions-label">Quick Create:</span>
                  <button className="btn-sm btn-outline" onClick={createDefaultSubscribeVerifyTemplate}>
                    <i className="fas fa-user-check"></i>
                    SUBSCRIBE_VERIFY
                  </button>
                  <button className="btn-sm btn-outline" onClick={createDefaultAnomalyTemplate}>
                    <i className="fas fa-bell"></i>
                    Anomaly Alert
                  </button>
                </div>
                <div className="items-grid">
                {templates.map((template) => (
                  <div key={template._id} className="item-card template-card">
                    <div className="item-header">
                      <h3>{template._name || 'Untitled Template'}</h3>
                      <div className="item-actions">
                        <button className="btn-icon" onClick={() => editTemplate(template)} title="Edit">
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="btn-icon danger" onClick={() => deleteTemplate(template)} title="Delete">
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    {template._description && <p className="item-description">{template._description}</p>}
                    <div className="item-badges">
                      <span className={`badge ${template._transport?.toLowerCase()}`}>
                        {template._transport}
                      </span>
                      <span className="badge format">{template._format}</span>
                    </div>
                    {(template._email?._subject || template._push?._title) && (
                      <div className="template-subject">
                        <strong>Subject:</strong> {template._email?._subject || template._push?._title}
                      </div>
                    )}

                    {/* Namespaces (immutable after creation) */}
                    <div className="item-namespaces">
                      <div className="namespaces-header">
                        <span className="namespaces-label">
                          <i className="fas fa-globe"></i>
                          Namespaces
                        </span>
                      </div>
                      <div className="namespace-tags">
                        {(template._namespaces || []).map(ns => (
                          <span key={ns} className="namespace-tag">{ns}</span>
                        ))}
                        {(!template._namespaces || template._namespaces.length === 0) && (
                          <span className="namespace-tag none">No namespaces</span>
                        )}
                      </div>
                    </div>

                    <div className="item-meta">
                      <code className="item-id">{template._id}</code>
                    </div>
                  </div>
                ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Triggers Tab */}
        {activeTab === 'triggers' && (
          <div className="section-card">
            <div className="section-header">
              <h2>
                <i className="fas fa-bolt"></i>
                Event Triggers
              </h2>
              <button className="btn-primary" onClick={() => { initTriggerForm(); initCreationNamespaces(); setIsCreating(true); }}>
                <i className="fas fa-plus"></i>
                Add Trigger
              </button>
            </div>
            <p className="section-description">
              Triggers watch for events (like new readings) and automatically send notifications
            </p>

            {triggers.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>No triggers found</p>
                <p className="empty-hint">Create a trigger to automatically notify when events occur</p>
              </div>
            ) : (
              <div className="items-grid">
                {triggers.map((trigger) => (
                  <div key={trigger._id} className="item-card trigger-card">
                    <div className="item-header">
                      <h3>{trigger._name || 'Untitled Trigger'}</h3>
                      <div className="item-actions">
                        <button className="btn-icon" onClick={() => editTrigger(trigger)} title="Edit">
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="btn-icon danger" onClick={() => deleteTrigger(trigger)} title="Delete">
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <div className="item-badges">
                      <span className="badge trigger-type">{trigger._type}</span>
                    </div>
                    {trigger._event?._filters && (
                      <div className="trigger-filters">
                        <strong>Watches:</strong>
                        <code>{trigger._event._filters.event?.split('.').pop()}</code>
                        <code>{trigger._event._filters['resource.type']?.split('.').pop()}</code>
                      </div>
                    )}
                    <div className="trigger-targets">
                      {trigger._groups && trigger._groups.length > 0 && (
                        <span><i className="fas fa-layer-group"></i> {trigger._groups.length} group(s)</span>
                      )}
                      {trigger._templates && trigger._templates.length > 0 && (
                        <span><i className="fas fa-file-alt"></i> {trigger._templates.length} template(s)</span>
                      )}
                    </div>
                    {(() => {
                      const scriptData = trigger._script as { id?: string; name?: string; userType?: string; versionId?: string } | undefined;
                      return scriptData?.userType ? (
                        <div className="trigger-script">
                          <i className="fas fa-code"></i>
                          <span className="script-usertype">{scriptData.userType}</span>
                          {scriptData.name && <span className="script-function">→ {scriptData.name}()</span>}
                        </div>
                      ) : null;
                    })()}
                    <div className="item-meta">
                      <code className="item-id">{trigger._id}</code>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Senders Tab */}
        {activeTab === 'senders' && (
          <div className="section-card">
            <div className="section-header">
              <h2>
                <i className="fas fa-paper-plane"></i>
                Senders
              </h2>
              <button className="btn-primary" onClick={() => { setSenderForm(getEmptySenderForm()); initCreationNamespaces(); setIsCreating(true); }}>
                <i className="fas fa-plus"></i>
                Add Sender
              </button>
            </div>
            <p className="section-description">
              Senders define the "from" identity (email address, display name) for outgoing notifications
            </p>

            {senders.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>No senders found</p>
                <p className="empty-hint">Create a sender to define the "from" address for notifications</p>
              </div>
            ) : (
              <div className="items-grid">
                {senders.map((sender) => (
                  <div key={sender._id} className="item-card sender-card">
                    <div className="item-header">
                      <h3>{sender._email?._displayName || sender._email?._address || 'Untitled Sender'}</h3>
                      <div className="item-actions">
                        <button className="btn-icon" onClick={() => editSender(sender)} title="Edit">
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="btn-icon danger" onClick={() => deleteSender(sender)} title="Delete">
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    {sender._email?._address && (
                      <p className="item-description">
                        <i className="fas fa-envelope"></i> {sender._email._address}
                      </p>
                    )}
                    <div className="item-badges">
                      <span className={`badge ${sender._transport?.toLowerCase()}`}>
                        {sender._transport}
                      </span>
                      {sender._templates?.SUBSCRIBE_VERIFY && (
                        <span className="badge template-linked" title="Has SUBSCRIBE_VERIFY template">
                          <i className="fas fa-file-alt"></i> Verify Template
                        </span>
                      )}
                    </div>

                    {/* Namespaces (immutable after creation) */}
                    <div className="item-namespaces">
                      <div className="namespaces-header">
                        <span className="namespaces-label">
                          <i className="fas fa-globe"></i>
                          Namespaces
                        </span>
                      </div>
                      <div className="namespace-tags">
                        {(sender._namespaces || []).map(ns => (
                          <span key={ns} className="namespace-tag">{ns}</span>
                        ))}
                        {(!sender._namespaces || sender._namespaces.length === 0) && (
                          <span className="namespace-tag none">No namespaces</span>
                        )}
                      </div>
                    </div>

                    <div className="item-meta">
                      <code className="item-id">{sender._id}</code>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {isCreating && (
        <div className="modal-overlay" onClick={() => setIsCreating(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-plus-circle"></i>
                {activeTab === 'groups' && 'Create Group'}
                {activeTab === 'devices' && 'Add Device'}
                {activeTab === 'subscriptions' && 'Create Subscription'}
                {activeTab === 'templates' && 'Create Template'}
                {activeTab === 'triggers' && 'Create Trigger'}
                {activeTab === 'senders' && 'Create Sender'}
              </h2>
              <button className="btn-icon" onClick={() => setIsCreating(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              {/* Group Form */}
              {activeTab === 'groups' && (
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Name*</label>
                    <input
                      type="text"
                      value={groupForm.name}
                      onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                      placeholder="e.g., Anomaly Alerts"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Description</label>
                    <textarea
                      value={groupForm.description}
                      onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                      placeholder="Describe this notification group..."
                      rows={3}
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Namespaces* <span className="form-hint">(immutable after creation)</span></label>
                    <div className="namespace-creator">
                      <div className="namespace-tags">
                        {creationNamespaces.map(ns => (
                          <span key={ns} className="namespace-tag editing">
                            {ns}
                            <button type="button" onClick={() => removeNamespaceFromCreation(ns)}>×</button>
                          </span>
                        ))}
                      </div>
                      <div className="namespace-add">
                        <input
                          type="text"
                          placeholder="Add namespace..."
                          value={newNamespaceInput}
                          onChange={(e) => setNewNamespaceInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addNamespaceToCreation())}
                        />
                        <button type="button" className="btn-icon" onClick={addNamespaceToCreation}>
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Device Form */}
              {activeTab === 'devices' && (
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Transport Type*</label>
                    <select
                      value={deviceForm.transport}
                      onChange={(e) => {
                        setDeviceForm({ ...deviceForm, transport: e.target.value as 'EMAIL' | 'PUSH' | 'WEBSOCKET' | 'WEBHOOK' });
                        setSelectedUserId(''); // Reset user selection when transport changes
                      }}
                    >
                      <option value="EMAIL">Email</option>
                      <option value="PUSH">Push Notification</option>
                      <option value="WEBSOCKET">WebSocket</option>
                      <option value="WEBHOOK">Webhook</option>
                    </select>
                    <span className="form-hint">Currently only EMAIL transport is fully supported</span>
                  </div>
                  
                  {/* EMAIL transport - User selection from user group */}
                  {deviceForm.transport === 'EMAIL' && (
                    <>
                      <div className="form-group full-width">
                        <label>Select User from User Group*</label>
                        {!selectedUserGroup ? (
                          <div className="form-warning">
                            <i className="fas fa-exclamation-triangle"></i>
                            <span>Please select a User Group first (in the header dropdown)</span>
                          </div>
                        ) : userGroupMembers.length === 0 ? (
                          <div className="form-warning">
                            <i className="fas fa-info-circle"></i>
                            <span>No users found in the selected user group "{selectedUserGroup._name}"</span>
                          </div>
                        ) : (
                          <select
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                          >
                            <option value="">Select a user...</option>
                            {userGroupMembers.map((member) => (
                              <option key={member._id} value={member._id}>
                                {member._displayName || `${member._firstName || ''} ${member._lastName || ''}`.trim() || member._email || member._id}
                                {member._email && ` (${member._email})`}
                              </option>
                            ))}
                          </select>
                        )}
                        <span className="form-hint">
                          <i className="fas fa-info-circle"></i>
                          Email devices must be linked to existing project users. The user's email and IRN will be auto-populated.
                        </span>
                      </div>
                      {selectedUserId && (
                        <div className="form-group full-width">
                          <label>Selected User Details</label>
                          <div className="user-preview">
                            {(() => {
                              const member = userGroupMembers.find(u => u._id === selectedUserId);
                              return member ? (
                                <>
                                  <div><strong>Email:</strong> {member._email || 'N/A'}</div>
                                  <div><strong>IRN:</strong> passportsvc:user:{member._id}</div>
                                </>
                              ) : null;
                            })()}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Non-EMAIL transports - Manual input */}
                  {deviceForm.transport !== 'EMAIL' && (
                    <>
                      <div className="form-group">
                        <label>Address*</label>
                        <input
                          type="text"
                          value={deviceForm.address}
                          onChange={(e) => setDeviceForm({ ...deviceForm, address: e.target.value })}
                          placeholder={deviceForm.transport === 'WEBHOOK' ? 'https://example.com/webhook' : 'Address'}
                        />
                      </div>
                      <div className="form-group">
                        <label>Owner IRN (optional)</label>
                        <input
                          type="text"
                          value={deviceForm.ownerIrn}
                          onChange={(e) => setDeviceForm({ ...deviceForm, ownerIrn: e.target.value })}
                          placeholder="passportsvc:user:userid"
                        />
                        <span className="form-hint">Link this device to a specific user</span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Subscription Form */}
              {activeTab === 'subscriptions' && (
                <div className="form-grid">
                  <div className="form-group">
                    <label>Group*</label>
                    <select
                      value={subscriptionGroupId}
                      onChange={(e) => setSubscriptionGroupId(e.target.value)}
                    >
                      <option value="">Select a group...</option>
                      {groups.map((g) => (
                        <option key={g._id} value={g._id}>{g._name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Device*</label>
                    <select
                      value={subscriptionDeviceId}
                      onChange={(e) => setSubscriptionDeviceId(e.target.value)}
                    >
                      <option value="">Select a device...</option>
                      {devices.map((d) => (
                        <option key={d._id} value={d._id}>{getDeviceAddress(d)} ({d._transport})</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label>Namespaces* <span className="form-hint">(immutable after creation)</span></label>
                    <div className="namespace-creator">
                      <div className="namespace-tags">
                        {creationNamespaces.map(ns => (
                          <span key={ns} className="namespace-tag editing">
                            {ns}
                            <button type="button" onClick={() => removeNamespaceFromCreation(ns)}>×</button>
                          </span>
                        ))}
                      </div>
                      <div className="namespace-add">
                        <input
                          type="text"
                          placeholder="Add namespace..."
                          value={newNamespaceInput}
                          onChange={(e) => setNewNamespaceInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addNamespaceToCreation())}
                        />
                        <button type="button" className="btn-icon" onClick={addNamespaceToCreation}>
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Template Form */}
              {activeTab === 'templates' && (
                <div className="form-grid">
                  <div className="form-group">
                    <label>Name*</label>
                    <input
                      type="text"
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                      placeholder="Template name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Transport</label>
                    <select
                      value={templateForm.transport}
                      onChange={(e) => setTemplateForm({ ...templateForm, transport: e.target.value as 'EMAIL' | 'PUSH' | 'WEBSOCKET' | 'WEBHOOK' })}
                    >
                      <option value="EMAIL">Email</option>
                      <option value="PUSH">Push Notification</option>
                      <option value="WEBSOCKET">WebSocket</option>
                      <option value="WEBHOOK">Webhook</option>
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label>Description</label>
                    <input
                      type="text"
                      value={templateForm.description}
                      onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                      placeholder="Template description"
                    />
                  </div>
                  <div className="form-group">
                    <label>Format</label>
                    <select
                      value={templateForm.format}
                      onChange={(e) => setTemplateForm({ ...templateForm, format: e.target.value as 'VELOCITY' | 'HANDLEBARS' })}
                    >
                      <option value="VELOCITY">Velocity</option>
                      <option value="HANDLEBARS">Handlebars</option>
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label>Subject*</label>
                    <input
                      type="text"
                      value={templateForm.subject}
                      onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                      placeholder="e.g., [Alert] ${request.resource.type} - ${group.description}"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Body (HTML)</label>
                    <textarea
                      value={templateForm.body}
                      onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                      placeholder="Email body with HTML and template variables..."
                      rows={10}
                      className="code-textarea"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Namespaces* <span className="form-hint">(immutable after creation)</span></label>
                    <div className="namespace-creator">
                      <div className="namespace-tags">
                        {creationNamespaces.map(ns => (
                          <span key={ns} className="namespace-tag editing">
                            {ns}
                            <button type="button" onClick={() => removeNamespaceFromCreation(ns)}>×</button>
                          </span>
                        ))}
                      </div>
                      <div className="namespace-add">
                        <input
                          type="text"
                          placeholder="Add namespace..."
                          value={newNamespaceInput}
                          onChange={(e) => setNewNamespaceInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addNamespaceToCreation())}
                        />
                        <button type="button" className="btn-icon" onClick={addNamespaceToCreation}>
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="form-group full-width template-variables-help">
                    <label><i className="fas fa-info-circle"></i> Available Template Variables (Velocity syntax)</label>
                    <div className="variables-grid">
                      <div className="variable-group">
                        <strong>Event Data</strong>
                        <code>${'${request.event}'}</code> Event type
                        <code>${'${request.resource.type}'}</code> Resource type
                      </div>
                      <div className="variable-group">
                        <strong>Context</strong>
                        <code>${'${group.description}'}</code> Group description
                        <code>${'${sender.email.address}'}</code> Sender email
                        <code>${'${device.type}'}</code> Device type
                      </div>
                      <div className="variable-group">
                        <strong>Links (REQUIRED in emails)</strong>
                        <code>${'${cancelLink}'}</code> Unsubscribe from group
                        <code>${'${cancelAllLink}'}</code> Unsubscribe from all
                        <code>${'${acceptLink}'}</code> Accept subscription
                      </div>
                    </div>
                    <span className="form-hint warning">
                      <i className="fas fa-exclamation-triangle"></i> Email templates MUST include ${'{cancelLink}'} and ${'{cancelAllLink}'} for unsubscribe links!
                    </span>
                  </div>
                </div>
              )}

              {/* Trigger Form */}
              {activeTab === 'triggers' && (
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Name*</label>
                    <input
                      type="text"
                      value={triggerForm.name}
                      onChange={(e) => setTriggerForm({ ...triggerForm, name: e.target.value })}
                      placeholder="e.g., ReadingCreated-Anomaly"
                    />
                  </div>

                  <div className="form-group">
                    <label>Event Type</label>
                    <select
                      value={triggerForm.eventType}
                      onChange={(e) => setTriggerForm({ ...triggerForm, eventType: e.target.value })}
                    >
                      {EVENT_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Resource Type</label>
                    <select
                      value={triggerForm.resourceType}
                      onChange={(e) => setTriggerForm({ ...triggerForm, resourceType: e.target.value })}
                    >
                      {RESOURCE_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label>Resource Namespace (optional)</label>
                    <input
                      type="text"
                      value={triggerForm.resourceNamespace}
                      onChange={(e) => setTriggerForm({ ...triggerForm, resourceNamespace: e.target.value })}
                      placeholder="Leave empty to use project namespace"
                    />
                    <span className="form-hint">Filter events to a specific namespace</span>
                  </div>

                  <div className="form-group full-width">
                    <label>Namespaces* <span className="form-hint">(immutable after creation)</span></label>
                    <div className="namespace-creator">
                      <div className="namespace-tags">
                        {creationNamespaces.map(ns => (
                          <span key={ns} className="namespace-tag editing">
                            {ns}
                            <button type="button" onClick={() => removeNamespaceFromCreation(ns)}>×</button>
                          </span>
                        ))}
                      </div>
                      <div className="namespace-add">
                        <input
                          type="text"
                          placeholder="Add namespace..."
                          value={newNamespaceInput}
                          onChange={(e) => setNewNamespaceInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addNamespaceToCreation())}
                        />
                        <button type="button" className="btn-icon" onClick={addNamespaceToCreation}>
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label>Groups* (select at least one)</label>
                    <div className="checkbox-list">
                      {groups.map((g) => (
                        <label key={g._id} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={triggerForm.groupIds.includes(g._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setTriggerForm({ ...triggerForm, groupIds: [...triggerForm.groupIds, g._id] });
                              } else {
                                setTriggerForm({ ...triggerForm, groupIds: triggerForm.groupIds.filter(id => id !== g._id) });
                              }
                            }}
                          />
                          {g._name}
                        </label>
                      ))}
                      {groups.length === 0 && <span className="form-hint">No groups available. Create a group first.</span>}
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label>Templates* (select at least one)</label>
                    <div className="checkbox-list">
                      {templates.map((t) => (
                        <label key={t._id} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={triggerForm.templateIds.includes(t._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setTriggerForm({ ...triggerForm, templateIds: [...triggerForm.templateIds, t._id] });
                              } else {
                                setTriggerForm({ ...triggerForm, templateIds: triggerForm.templateIds.filter(id => id !== t._id) });
                              }
                            }}
                          />
                          {t._name} ({t._transport})
                        </label>
                      ))}
                      {templates.length === 0 && <span className="form-hint">No templates available. Create a template first.</span>}
                    </div>
                  </div>

                  {senders.length > 0 && (
                    <div className="form-group full-width">
                      <label>Senders (optional)</label>
                      <div className="checkbox-list">
                        {senders.map((s) => (
                          <label key={s._id} className="checkbox-item">
                            <input
                              type="checkbox"
                              checked={triggerForm.senderIds.includes(s._id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setTriggerForm({ ...triggerForm, senderIds: [...triggerForm.senderIds, s._id] });
                                } else {
                                  setTriggerForm({ ...triggerForm, senderIds: triggerForm.senderIds.filter(id => id !== s._id) });
                                }
                              }}
                            />
                            {s._email?._displayName || s._email?._address || s._id}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="form-group full-width">
                    <label>Notification Script (optional)</label>
                    <select
                      value={triggerForm.scriptUserType}
                      onChange={(e) => {
                        const selectedUserType = e.target.value;
                        const selectedScript = scripts.find(s => s._userType === selectedUserType);
                        setTriggerForm({ 
                          ...triggerForm, 
                          scriptUserType: selectedUserType,
                          // Auto-populate version ID from script's _tipId (current version)
                          scriptVersionId: selectedScript?._tipId || ''
                        });
                      }}
                    >
                      <option value="">None - Use event data directly</option>
                      {scripts
                        .filter(s => s._userType) // Only show scripts with _userType
                        .map((s) => (
                          <option key={s._id} value={s._userType}>
                            {s._userType} {s._name ? `(${s._name})` : ''} {s._description ? `- ${s._description}` : ''}
                          </option>
                        ))
                      }
                    </select>
                    <span className="form-hint">
                      <i className="fas fa-info-circle"></i> 
                      Script processes event data before passing to template. 
                      Selected by _userType (stable identifier, name/version may change).
                    </span>
                  </div>

                  {triggerForm.scriptUserType && (
                    <>
                      <div className="form-group">
                        <label>Script Function Name *</label>
                        <input
                          type="text"
                          value={triggerForm.scriptFunctionName}
                          onChange={(e) => setTriggerForm({ ...triggerForm, scriptFunctionName: e.target.value })}
                          placeholder="e.g., processNotifications"
                        />
                        <span className="form-hint">
                          <i className="fas fa-code"></i> 
                          Function to execute in the script.
                        </span>
                      </div>
                      <div className="form-group">
                        <label>Script Version ID *</label>
                        <input
                          type="text"
                          value={triggerForm.scriptVersionId}
                          onChange={(e) => setTriggerForm({ ...triggerForm, scriptVersionId: e.target.value })}
                          placeholder="e.g., abc123-def456..."
                        />
                        <span className="form-hint">
                          <i className="fas fa-tag"></i> 
                          Version ID of the script to use.
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Sender Form */}
              {activeTab === 'senders' && (
                <div className="form-grid">
                  <div className="form-group">
                    <label>Transport</label>
                    <select
                      value={senderForm.transport}
                      onChange={(e) => setSenderForm({ ...senderForm, transport: e.target.value as 'EMAIL' | 'PUSH' | 'WEBSOCKET' | 'WEBHOOK' })}
                    >
                      <option value="EMAIL">Email</option>
                    </select>
                    <span className="form-hint">Currently only EMAIL transport is supported</span>
                  </div>
                  <div className="form-group">
                    <label>Email Address*</label>
                    <input
                      type="email"
                      value={senderForm.emailAddress}
                      onChange={(e) => setSenderForm({ ...senderForm, emailAddress: e.target.value })}
                      placeholder="noreply@example.com"
                    />
                    <span className="form-hint">The "From:" address in sent emails</span>
                  </div>
                  <div className="form-group full-width">
                    <label>Display Name</label>
                    <input
                      type="text"
                      value={senderForm.displayName}
                      onChange={(e) => setSenderForm({ ...senderForm, displayName: e.target.value })}
                      placeholder="Notification System"
                    />
                    <span className="form-hint">The "From:" display name (e.g., "Acme Alerts")</span>
                  </div>

                  <div className="form-group full-width">
                    <label>Namespaces* <span className="form-hint">(immutable after creation)</span></label>
                    <div className="namespace-creator">
                      <div className="namespace-tags">
                        {creationNamespaces.map(ns => (
                          <span key={ns} className="namespace-tag editing">
                            {ns}
                            <button type="button" onClick={() => removeNamespaceFromCreation(ns)}>×</button>
                          </span>
                        ))}
                      </div>
                      <div className="namespace-add">
                        <input
                          type="text"
                          placeholder="Add namespace..."
                          value={newNamespaceInput}
                          onChange={(e) => setNewNamespaceInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addNamespaceToCreation())}
                        />
                        <button type="button" className="btn-icon" onClick={addNamespaceToCreation}>
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label>Subscription Verify Template (optional)</label>
                    <select
                      value={senderForm.subscribeVerifyTemplateId}
                      onChange={(e) => setSenderForm({ ...senderForm, subscribeVerifyTemplateId: e.target.value })}
                    >
                      <option value="">None - Skip verification</option>
                      {templates.map((t) => (
                        <option key={t._id} value={t._id}>{t._name} ({t._transport})</option>
                      ))}
                    </select>
                    <span className="form-hint">Template sent when a device subscribes to a group (should include $acceptLink)</span>
                  </div>

                  <div className="form-group full-width form-section-header">
                    <label><i className="fas fa-external-link-alt"></i> Redirect URIs</label>
                    <span className="form-hint">Where users are redirected after clicking links in emails</span>
                  </div>

                  <div className="form-group">
                    <label>Subscribe Success</label>
                    <input
                      type="url"
                      value={senderForm.redirectUris.subscribeSuccess}
                      onChange={(e) => setSenderForm({ 
                        ...senderForm, 
                        redirectUris: { ...senderForm.redirectUris, subscribeSuccess: e.target.value }
                      })}
                      placeholder="https://myapp.com/subscribed"
                    />
                  </div>
                  <div className="form-group">
                    <label>Subscribe Failure</label>
                    <input
                      type="url"
                      value={senderForm.redirectUris.subscribeFailure}
                      onChange={(e) => setSenderForm({ 
                        ...senderForm, 
                        redirectUris: { ...senderForm.redirectUris, subscribeFailure: e.target.value }
                      })}
                      placeholder="https://myapp.com/subscribe-error"
                    />
                  </div>
                  <div className="form-group">
                    <label>Unsubscribe Success</label>
                    <input
                      type="url"
                      value={senderForm.redirectUris.unsubscribeSuccess}
                      onChange={(e) => setSenderForm({ 
                        ...senderForm, 
                        redirectUris: { ...senderForm.redirectUris, unsubscribeSuccess: e.target.value }
                      })}
                      placeholder="https://myapp.com/unsubscribed"
                    />
                  </div>
                  <div className="form-group">
                    <label>Unsubscribe Failure</label>
                    <input
                      type="url"
                      value={senderForm.redirectUris.unsubscribeFailure}
                      onChange={(e) => setSenderForm({ 
                        ...senderForm, 
                        redirectUris: { ...senderForm.redirectUris, unsubscribeFailure: e.target.value }
                      })}
                      placeholder="https://myapp.com/unsubscribe-error"
                    />
                  </div>
                  <div className="form-group">
                    <label>Unsubscribe All Success</label>
                    <input
                      type="url"
                      value={senderForm.redirectUris.unsubscribeAllSuccess}
                      onChange={(e) => setSenderForm({ 
                        ...senderForm, 
                        redirectUris: { ...senderForm.redirectUris, unsubscribeAllSuccess: e.target.value }
                      })}
                      placeholder="https://myapp.com/unsubscribed-all"
                    />
                  </div>
                  <div className="form-group">
                    <label>Unsubscribe All Failure</label>
                    <input
                      type="url"
                      value={senderForm.redirectUris.unsubscribeAllFailure}
                      onChange={(e) => setSenderForm({ 
                        ...senderForm, 
                        redirectUris: { ...senderForm.redirectUris, unsubscribeAllFailure: e.target.value }
                      })}
                      placeholder="https://myapp.com/unsubscribe-all-error"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setIsCreating(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  if (activeTab === 'groups') createGroup();
                  else if (activeTab === 'devices') createDevice();
                  else if (activeTab === 'subscriptions') createSubscription();
                  else if (activeTab === 'templates') createTemplate();
                  else if (activeTab === 'triggers') createTrigger();
                  else if (activeTab === 'senders') createSender();
                }}
                disabled={isLoading}
              >
                {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-plus"></i>}
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Modal */}
      {isEditing && editingTemplateId && (
        <div className="modal-overlay" onClick={() => { setIsEditing(false); setEditingTemplateId(null); setTemplateForm(getEmptyTemplateForm()); }}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-edit"></i>
                Edit Template
              </h2>
              <button className="btn-icon" onClick={() => { setIsEditing(false); setEditingTemplateId(null); setTemplateForm(getEmptyTemplateForm()); }}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Name*</label>
                  <input
                    type="text"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                    placeholder="Template name"
                  />
                </div>
                <div className="form-group">
                  <label>Transport</label>
                  <select
                    value={templateForm.transport}
                    onChange={(e) => setTemplateForm({ ...templateForm, transport: e.target.value as 'EMAIL' | 'PUSH' | 'WEBSOCKET' | 'WEBHOOK' })}
                  >
                    <option value="EMAIL">Email</option>
                    <option value="PUSH">Push Notification</option>
                    <option value="WEBSOCKET">WebSocket</option>
                    <option value="WEBHOOK">Webhook</option>
                  </select>
                </div>
                <div className="form-group full-width">
                  <label>Description</label>
                  <input
                    type="text"
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                    placeholder="Template description"
                  />
                </div>
                <div className="form-group">
                  <label>Format</label>
                  <select
                    value={templateForm.format}
                    onChange={(e) => setTemplateForm({ ...templateForm, format: e.target.value as 'VELOCITY' | 'HANDLEBARS' })}
                  >
                    <option value="VELOCITY">Velocity</option>
                    <option value="HANDLEBARS">Handlebars</option>
                  </select>
                </div>
                <div className="form-group full-width">
                  <label>Subject*</label>
                  <input
                    type="text"
                    value={templateForm.subject}
                    onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                    placeholder="e.g., [Alert] ${request.resource.type} - ${group.description}"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Body (HTML)</label>
                  <textarea
                    value={templateForm.body}
                    onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                    placeholder="Email body with HTML and template variables..."
                    rows={12}
                    className="code-textarea"
                  />
                </div>
                <div className="form-group full-width template-variables-help">
                  <label><i className="fas fa-info-circle"></i> Available Template Variables (Velocity syntax)</label>
                  <div className="variables-grid">
                    <div className="variable-group">
                      <strong>Event Data</strong>
                      <code>${'${request.event}'}</code> Event type
                      <code>${'${request.resource.type}'}</code> Resource type
                    </div>
                    <div className="variable-group">
                      <strong>Context</strong>
                      <code>${'${group.description}'}</code> Group description
                      <code>${'${sender.email.address}'}</code> Sender email
                      <code>${'${device.type}'}</code> Device type
                    </div>
                    <div className="variable-group">
                      <strong>Links (REQUIRED in emails)</strong>
                      <code>${'${cancelLink}'}</code> Unsubscribe from group
                      <code>${'${cancelAllLink}'}</code> Unsubscribe from all
                      <code>${'${acceptLink}'}</code> Accept subscription
                    </div>
                  </div>
                  <span className="form-hint warning">
                    <i className="fas fa-exclamation-triangle"></i> Email templates MUST include ${'{cancelLink}'} and ${'{cancelAllLink}'} for unsubscribe links!
                  </span>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setIsEditing(false); setEditingTemplateId(null); setTemplateForm(getEmptyTemplateForm()); }}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={updateTemplate}
                disabled={isLoading}
              >
                {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Sender Modal */}
      {isEditing && editingSenderId && (
        <div className="modal-overlay" onClick={() => { setIsEditing(false); setEditingSenderId(null); setSenderForm(getEmptySenderForm()); }}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-edit"></i>
                Edit Sender
              </h2>
              <button className="btn-icon" onClick={() => { setIsEditing(false); setEditingSenderId(null); setSenderForm(getEmptySenderForm()); }}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Email Address*</label>
                  <input
                    type="email"
                    value={senderForm.emailAddress}
                    onChange={(e) => setSenderForm({ ...senderForm, emailAddress: e.target.value })}
                    placeholder="noreply@example.com"
                  />
                  <span className="form-hint">The "From:" address in sent emails</span>
                </div>
                <div className="form-group">
                  <label>Display Name</label>
                  <input
                    type="text"
                    value={senderForm.displayName}
                    onChange={(e) => setSenderForm({ ...senderForm, displayName: e.target.value })}
                    placeholder="Notification System"
                  />
                  <span className="form-hint">The "From:" display name (e.g., "Acme Alerts")</span>
                </div>

                <div className="form-group full-width">
                  <label>SUBSCRIBE_VERIFY Template</label>
                  <select
                    value={senderForm.subscribeVerifyTemplateId}
                    onChange={(e) => setSenderForm({ ...senderForm, subscribeVerifyTemplateId: e.target.value })}
                  >
                    <option value="">None - Skip verification</option>
                    {templates.map((t) => (
                      <option key={t._id} value={t._id}>{t._name} ({t._transport})</option>
                    ))}
                  </select>
                  <span className="form-hint">Template sent when a device subscribes (should include $acceptLink)</span>
                </div>

                <div className="form-group full-width form-section-header">
                  <label><i className="fas fa-external-link-alt"></i> Redirect URIs</label>
                  <span className="form-hint">Where users are redirected after clicking links in emails</span>
                </div>

                <div className="form-group">
                  <label>Subscribe Success</label>
                  <input
                    type="url"
                    value={senderForm.redirectUris.subscribeSuccess}
                    onChange={(e) => setSenderForm({ 
                      ...senderForm, 
                      redirectUris: { ...senderForm.redirectUris, subscribeSuccess: e.target.value }
                    })}
                    placeholder="https://myapp.com/subscribed"
                  />
                </div>
                <div className="form-group">
                  <label>Subscribe Failure</label>
                  <input
                    type="url"
                    value={senderForm.redirectUris.subscribeFailure}
                    onChange={(e) => setSenderForm({ 
                      ...senderForm, 
                      redirectUris: { ...senderForm.redirectUris, subscribeFailure: e.target.value }
                    })}
                    placeholder="https://myapp.com/subscribe-error"
                  />
                </div>
                <div className="form-group">
                  <label>Unsubscribe Success</label>
                  <input
                    type="url"
                    value={senderForm.redirectUris.unsubscribeSuccess}
                    onChange={(e) => setSenderForm({ 
                      ...senderForm, 
                      redirectUris: { ...senderForm.redirectUris, unsubscribeSuccess: e.target.value }
                    })}
                    placeholder="https://myapp.com/unsubscribed"
                  />
                </div>
                <div className="form-group">
                  <label>Unsubscribe Failure</label>
                  <input
                    type="url"
                    value={senderForm.redirectUris.unsubscribeFailure}
                    onChange={(e) => setSenderForm({ 
                      ...senderForm, 
                      redirectUris: { ...senderForm.redirectUris, unsubscribeFailure: e.target.value }
                    })}
                    placeholder="https://myapp.com/unsubscribe-error"
                  />
                </div>
                <div className="form-group">
                  <label>Unsubscribe All Success</label>
                  <input
                    type="url"
                    value={senderForm.redirectUris.unsubscribeAllSuccess}
                    onChange={(e) => setSenderForm({ 
                      ...senderForm, 
                      redirectUris: { ...senderForm.redirectUris, unsubscribeAllSuccess: e.target.value }
                    })}
                    placeholder="https://myapp.com/unsubscribed-all"
                  />
                </div>
                <div className="form-group">
                  <label>Unsubscribe All Failure</label>
                  <input
                    type="url"
                    value={senderForm.redirectUris.unsubscribeAllFailure}
                    onChange={(e) => setSenderForm({ 
                      ...senderForm, 
                      redirectUris: { ...senderForm.redirectUris, unsubscribeAllFailure: e.target.value }
                    })}
                    placeholder="https://myapp.com/unsubscribe-all-error"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setIsEditing(false); setEditingSenderId(null); setSenderForm(getEmptySenderForm()); }}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={updateSender}
                disabled={isLoading}
              >
                {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Trigger Modal */}
      {isEditing && editingTriggerId && (
        <div className="modal-overlay" onClick={() => { setIsEditing(false); setEditingTriggerId(null); setTriggerForm(getEmptyTriggerForm()); }}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-edit"></i>
                Edit Trigger
              </h2>
              <button className="btn-icon" onClick={() => { setIsEditing(false); setEditingTriggerId(null); setTriggerForm(getEmptyTriggerForm()); }}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Name*</label>
                  <input
                    type="text"
                    value={triggerForm.name}
                    onChange={(e) => setTriggerForm({ ...triggerForm, name: e.target.value })}
                    placeholder="My Trigger"
                  />
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={triggerForm.type}
                    onChange={(e) => setTriggerForm({ ...triggerForm, type: e.target.value as 'EVENT' | 'SCHEDULE' })}
                  >
                    <option value="EVENT">Event-based</option>
                    <option value="SCHEDULE">Scheduled</option>
                  </select>
                </div>

                {triggerForm.type === 'EVENT' && (
                  <>
                    <div className="form-group">
                      <label>Event Type</label>
                      <select
                        value={triggerForm.eventType}
                        onChange={(e) => setTriggerForm({ ...triggerForm, eventType: e.target.value })}
                      >
                        <option value="">Select event type...</option>
                        {EVENT_TYPES.map(et => (
                          <option key={et.value} value={et.value}>{et.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Resource Type</label>
                      <select
                        value={triggerForm.resourceType}
                        onChange={(e) => setTriggerForm({ ...triggerForm, resourceType: e.target.value })}
                      >
                        <option value="">Select resource type...</option>
                        {RESOURCE_TYPES.map(rt => (
                          <option key={rt.value} value={rt.value}>{rt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group full-width">
                      <label>Resource Namespace</label>
                      <input
                        type="text"
                        value={triggerForm.resourceNamespace}
                        onChange={(e) => setTriggerForm({ ...triggerForm, resourceNamespace: e.target.value })}
                        placeholder="e.g., MyProject_AbC123"
                      />
                      <span className="form-hint">Filter events to a specific namespace</span>
                    </div>
                  </>
                )}

                <div className="form-group full-width">
                  <label>Groups* (select at least one)</label>
                  <div className="checkbox-list">
                    {groups.map((g) => (
                      <label key={g._id} className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={triggerForm.groupIds.includes(g._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTriggerForm({ ...triggerForm, groupIds: [...triggerForm.groupIds, g._id] });
                            } else {
                              setTriggerForm({ ...triggerForm, groupIds: triggerForm.groupIds.filter(id => id !== g._id) });
                            }
                          }}
                        />
                        {g._name}
                      </label>
                    ))}
                    {groups.length === 0 && <span className="form-hint">No groups available.</span>}
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>Templates* (select at least one)</label>
                  <div className="checkbox-list">
                    {templates.map((t) => (
                      <label key={t._id} className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={triggerForm.templateIds.includes(t._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTriggerForm({ ...triggerForm, templateIds: [...triggerForm.templateIds, t._id] });
                            } else {
                              setTriggerForm({ ...triggerForm, templateIds: triggerForm.templateIds.filter(id => id !== t._id) });
                            }
                          }}
                        />
                        {t._name} ({t._transport})
                      </label>
                    ))}
                    {templates.length === 0 && <span className="form-hint">No templates available.</span>}
                  </div>
                </div>

                {senders.length > 0 && (
                  <div className="form-group full-width">
                    <label>Senders (optional)</label>
                    <div className="checkbox-list">
                      {senders.map((s) => (
                        <label key={s._id} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={triggerForm.senderIds.includes(s._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setTriggerForm({ ...triggerForm, senderIds: [...triggerForm.senderIds, s._id] });
                              } else {
                                setTriggerForm({ ...triggerForm, senderIds: triggerForm.senderIds.filter(id => id !== s._id) });
                              }
                            }}
                          />
                          {s._email?._displayName || s._email?._address || s._id}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="form-group full-width">
                  <label>Notification Script (optional)</label>
                  <select
                    value={triggerForm.scriptUserType}
                    onChange={(e) => {
                      const selectedUserType = e.target.value;
                      const selectedScript = scripts.find(s => s._userType === selectedUserType);
                      setTriggerForm({ 
                        ...triggerForm, 
                        scriptUserType: selectedUserType,
                        // Auto-populate version ID from script's _tipId (current version)
                        scriptVersionId: selectedScript?._tipId || triggerForm.scriptVersionId
                      });
                    }}
                  >
                    <option value="">None - Use event data directly</option>
                    {scripts
                      .filter(s => s._userType)
                      .map((s) => (
                        <option key={s._id} value={s._userType}>
                          {s._userType} {s._name ? `(${s._name})` : ''} {s._description ? `- ${s._description}` : ''}
                        </option>
                      ))
                    }
                  </select>
                  <span className="form-hint">
                    <i className="fas fa-info-circle"></i> Script processes event data before passing to template.
                  </span>
                </div>

                {triggerForm.scriptUserType && (
                  <>
                    <div className="form-group">
                      <label>Script Function Name *</label>
                      <input
                        type="text"
                        value={triggerForm.scriptFunctionName}
                        onChange={(e) => setTriggerForm({ ...triggerForm, scriptFunctionName: e.target.value })}
                        placeholder="e.g., processNotifications"
                      />
                      <span className="form-hint">
                        <i className="fas fa-code"></i> 
                        Function to execute in the script.
                      </span>
                    </div>
                    <div className="form-group">
                      <label>Script Version ID *</label>
                      <input
                        type="text"
                        value={triggerForm.scriptVersionId}
                        onChange={(e) => setTriggerForm({ ...triggerForm, scriptVersionId: e.target.value })}
                        placeholder="e.g., abc123-def456..."
                      />
                      <span className="form-hint">
                        <i className="fas fa-tag"></i> 
                        Version ID of the script to use.
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setIsEditing(false); setEditingTriggerId(null); setTriggerForm(getEmptyTriggerForm()); }}>
                Cancel
              </button>
              <button
                className="btn-secondary"
                onClick={updateTriggerWithPut}
                disabled={isLoading}
                title="Use PUT method (full replacement) - try if PATCH fails"
              >
                {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-upload"></i>}
                Save (PUT)
              </button>
              <button
                className="btn-primary"
                onClick={updateTrigger}
                disabled={isLoading}
                title="Use PATCH method (partial update)"
              >
                {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                Save (PATCH)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationManagementPage;

